import React from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "hooks";
import { Box, IconButton, Typography } from "@material-ui/core";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";
import { TextField } from "../../../TextField/TextField.styled";
import { BriefingFormModel, Assets, MAIN_ASSETS_LENGTH } from "pages/BriefingForm/interfaces";
import { ArrayField, Controller } from "react-hook-form";
import { Dropzone } from "components";

interface FramesSubSectionProps extends FieldsHookForm<BriefingFormModel> {
    onUploadAsset: (acceptedFiles: File[], index: number) => void;
    onDeleteBriefFormAsset: (id: number) => void;
    fields: Partial<ArrayField<Record<string, Assets>, "id">>[];
}

export const FramesSubSection: React.FC<FramesSubSectionProps> = ({
    control,
    onUploadAsset,
    onDeleteBriefFormAsset,
    fields,
}) => {
    const { t } = useTranslation();
    const { toggleAlert } = useAlert();

    const handleDropFiles = (index: number) => (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 1) {
            return toggleAlert({
                type: "error",
                message: t("briefingForm.errors.oneFileOnly"),
            });
        }

        onUploadAsset(acceptedFiles, index);
    };

    return (
        <Box display="flex" flexDirection="column">
            {fields.slice(0, MAIN_ASSETS_LENGTH).map((asset, index) => (
                <Box key={asset.id || index} display="flex" alignItems="flex-end" justifyContent="space-between">
                    {asset.fileName ? (
                        <Box position="relative" mr={16} mb={2}>
                            <Box>
                                <Controller
                                    as={<TextField />}
                                    name={`digitalBrief.assets[${index}].id`}
                                    control={control}
                                    label={t("briefingForm.label.frameId")}
                                    disabled
                                />
                            </Box>
                            <Box position="absolute" top="7px" right="-15px">
                                <IconButton onClick={() => onDeleteBriefFormAsset(parseInt(asset.id as string))}>
                                    <DeleteOutlinedIcon color="error" />
                                </IconButton>
                            </Box>
                            <Box>
                                <Controller
                                    as={<TextField />}
                                    name={`digitalBrief.assets[${index}].fileName`}
                                    control={control}
                                    label={t("briefingForm.label.fileName")}
                                    disabled
                                />
                            </Box>
                        </Box>
                    ) : (
                        <Box mb={2} mr={16}>
                            <Typography variant="subtitle1">
                                {t("briefingForm.label.frameX", { number: index + 1 })}
                            </Typography>
                            <Dropzone
                                sizeX={200}
                                sizeY={100}
                                showDimensions={false}
                                label={t("briefingForm.label.dropText")}
                                onDrop={handleDropFiles(index)}
                            />
                        </Box>
                    )}

                    <Box mb="7px" width="100%">
                        <Controller
                            as={<TextField />}
                            name={`digitalBrief.assets[${index}].copyText`}
                            control={control}
                            multiline
                            rows={5}
                            variant="outlined"
                            label={t("briefingForm.label.copyText")}
                            fullWidth
                        />
                    </Box>
                </Box>
            ))}
        </Box>
    );
};
